import { Alert, Box, Button, Stack, TextField, Typography, alpha, useTheme } from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/apiClient';
import { useAuth } from '../state/AuthContext';
import { parseErrorMessage } from '../utils/errorUtils';
import AuthLayout from '../components/AuthLayout';
import CustomButton from '../components/CustomButton';

import type { Theme } from '@mui/material/styles';

const getInputSx = (theme: Theme) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    fontSize: '1rem',
    '& fieldset': { borderColor: theme.palette.divider, borderWidth: '2px' },
    '&:hover fieldset': { borderColor: theme.palette.text.secondary },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
      borderWidth: '2px',
      boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
    },
    '&.Mui-disabled fieldset': { borderColor: theme.palette.divider, opacity: 0.5 },
  },
  '& .MuiInputLabel-root': { display: 'none' },
  '& .MuiInputBase-input': {
    padding: '0.75rem 1rem',
    '&::placeholder': { color: theme.palette.text.secondary, opacity: 1 },
  },
  '& .MuiInputBase-input.Mui-disabled': {
    WebkitTextFillColor: theme.palette.text.disabled,
  },
});

export default function ForcePasswordChangePage() {
  const { user, completePasswordChange, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!user) {
      setError(t('forcePasswordChange.errors.sessionExpired'));
      return;
    }
    if (!currentPassword.trim() || !newPassword.trim()) {
      setError(t('forcePasswordChange.errors.missingFields'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t('forcePasswordChange.errors.mismatch'));
      return;
    }
    if (newPassword === currentPassword) {
      setError(t('forcePasswordChange.errors.samePassword'));
      return;
    }
    setError(null);
    setIsSubmitting(true);
    try {
      const response = await api.changePassword(null, {
        currentPassword: currentPassword.trim(),
        newPassword: newPassword.trim(),
      });
      completePasswordChange(response);
      navigate('/metrics', { replace: true });
    } catch (err) {
      setError(parseErrorMessage(err, t('forcePasswordChange.errors.generic')));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthLayout
      title={t('forcePasswordChange.title')}
      subtitle={t('forcePasswordChange.subtitle', {
        name: user?.displayName ?? user?.username ?? '',
      })}
    >
      <Box component="form" onSubmit={handleSubmit}>
        <Stack spacing={2}>
          {error && (
            <Alert severity="error" sx={{ borderRadius: '8px' }}>
              {error}
            </Alert>
          )}

          <Stack spacing={0.5}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: theme.palette.text.secondary, fontSize: '0.9375rem' }}
            >
              {t('forcePasswordChange.currentPassword')}
            </Typography>
            <TextField
              placeholder={t('forcePasswordChange.currentPasswordPlaceholder', 'Senha atual')}
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              disabled={isSubmitting}
              autoComplete="current-password"
              fullWidth
              sx={getInputSx(theme)}
            />
          </Stack>

          <Stack spacing={0.5}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: theme.palette.text.secondary, fontSize: '0.9375rem' }}
            >
              {t('forcePasswordChange.newPassword')}
            </Typography>
            <TextField
              placeholder={t('forcePasswordChange.newPasswordPlaceholder', 'Nova senha')}
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              disabled={isSubmitting}
              autoComplete="new-password"
              fullWidth
              sx={getInputSx(theme)}
            />
          </Stack>

          <Stack spacing={0.5}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: theme.palette.text.secondary, fontSize: '0.9375rem' }}
            >
              {t('forcePasswordChange.confirmPassword')}
            </Typography>
            <TextField
              placeholder={t(
                'forcePasswordChange.confirmPasswordPlaceholder',
                'Repita a nova senha',
              )}
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={isSubmitting}
              autoComplete="new-password"
              fullWidth
              sx={getInputSx(theme)}
            />
          </Stack>

          <Stack direction="column-reverse" spacing={2} justifyContent="space-between">
            <CustomButton variant="secondary" onClick={logout} disabled={isSubmitting}>
              {t('forcePasswordChange.logout')}
            </CustomButton>
            <CustomButton
              type="submit"
              variant="primary"
              disabled={
                isSubmitting ||
                !currentPassword.trim() ||
                !newPassword.trim() ||
                !confirmPassword.trim() ||
                newPassword !== confirmPassword
              }
            >
              {isSubmitting ? t('forcePasswordChange.submitting') : t('forcePasswordChange.submit')}
            </CustomButton>
          </Stack>
        </Stack>
      </Box>
    </AuthLayout>
  );
}
